// src/contracts/media/MediaPanelEvents.ts
//
// Phase M1 events.
// Hard rule: MediaPanel emits "timeline:add" only.
// Additional internal MediaPanel events remain optional.

import type { MediaAssetV1 } from "./MediaAssetV1";

export type TimelineAddProxyDescriptor = {
  id: string;
  kind: "timeline_proxy" | "scrub_proxy" | "waveform_proxy" | "preview_proxy";
  tier: "ultra_low" | "low" | "medium" | "high";
  status: "missing" | "queued" | "generating" | "ready" | "stale" | "failed" | "offline";
  uri?: string;
  resolution?: string;
  codec?: string;
  colorTransform?: string;
  timecodeParity?: "verified" | "unverified" | "broken";
  createdAt?: number;
  updatedAt?: number;
};

export type TimelineAddMetadataHistoryEntry = {
  id?: string;
  layer?: "source" | "normalized" | "semantic" | "user" | "system";
  at?: number;
  actor?: string;
  summary?: string;
  patch?: Record<string, unknown>;
};

export type TimelineAddTranscodeHistoryEntry = {
  id?: string;
  at?: number;
  tool?: string;
  sourceProxyId?: string;
  outputProxyId?: string;
  summary?: string;
  metadata?: Record<string, unknown>;
};

export const OMNICINE_MEDIA_SELECT_EVENT = "omnicine:media:select" as const;
// Not required, but useful for debugging / telemetry
export const OMNICINE_MEDIA_ADD_SELECTED_EVENT =
  "omnicine:media:addSelected" as const;
export const OMNICINE_MEDIA_PANEL_SET_MODE_EVENT = "omnicine:mediaPanel:setMode" as const;

export type OmniCineMediaSelectDetail = {
  assetId: string;
};

export type OmniCineMediaAddSelectedDetail = {
  asset: MediaAssetV1;
};

export type MediaPanelMode = "closed" | "ingest" | "focus" | "overlay";

export type MediaPanelSetModeDetail = {
  mode?: MediaPanelMode;
  pinned?: boolean;
  force?: boolean;
  preserveWitnessBar?: boolean;
  clipId?: string;
  trackId?: string;
  kind?: "video" | "audio" | "image" | "unknown";
  mediaId?: string;
  mediaUrl?: string;
  label?: string;
  clipLabel?: string;
  hasLinkedAudio?: boolean;
  transitionToken?: string;
  source?: "import" | "inlineWitness" | "system" | "mediaPanel";
};

export type TimelineAddDetail = {
  requestId?: string;
  trackId?: string;
  kind?: "video" | "audio" | "image" | "npc";
  durSec?: number;
  startSec?: number | string;
  endSec?: number | string;
  mediaId?: string;
  familyId?: string;
  originType?: "audioOnly" | "videoOnly" | "videoWithEmbeddedAudio" | "linkedMedia";
  hasPlayableAudio?: boolean;
  frameRate?: number;
  sampleRate?: number;
  channelLayout?: string;
  assetId?: string;
  sourceAssetId?: string;
  sourceHash?: string;
  containerFormat?: string;
  schemaVersion?: string;
  ingestId?: string;
  storagePath?: string;
  label?: string;
  sourceLabel?: string;
  fileName?: string;
  sourceFileName?: string;
  title?: string;
  scene?: string;
  take?: number | string;
  camera?: string;
  nativeWidth?: number;
  nativeHeight?: number;
  pixelAspectRatio?: number;
  displayAspectRatio?: number;
  colorSpace?: string;
  transferFunction?: string;
  hdrFormat?: string;
  interlaced?: boolean;
  videoBitDepth?: number;
  channelCount?: number;
  audioBitDepth?: number;
  audioCodec?: string;
  sourceInSec?: number;
  sourceOutSec?: number;
  timecodeStart?: string;
  dropFrame?: boolean;
  reelName?: string;
  streamLayout?: string;
  importSource?: string;
  ingestTimestamp?: number;
  metadataHistory?: TimelineAddMetadataHistoryEntry[];
  transcodeHistory?: TimelineAddTranscodeHistoryEntry[];
  offlineState?: "online" | "offline" | "missing" | "relinked" | "partial";
  growingFile?: boolean;
  analysisState?: "idle" | "queued" | "running" | "ready" | "failed" | "partial";
  metadataRichnessScore?: number;
  decodePerformanceScore?: number;
  proxyStatus?: "missing" | "queued" | "generating" | "ready" | "stale" | "failed" | "offline";
  preferredTimelineProxyId?: string;
  preferredScrubProxyId?: string;
  proxies?: TimelineAddProxyDescriptor[];
  waveformProxyUrl?: string;
  previewProxyUrl?: string;
  metadataVault?: Record<string, unknown>;
  sourceMetadata?: Record<string, unknown>;
  semanticMetadata?: Record<string, unknown>;
  userMetadata?: Record<string, unknown>;
  systemMetadata?: Record<string, unknown>;
  url?: string;
  previewUrl?: string;
  mediaUrl?: string;
  src?: string;
  source?: string;
  note?: string;
  ui?: { intent?: string };
};

// Canonical: TimelineShell listens to this only
export const TIMELINE_ADD_EVENT = "timeline:add" as const;

type WindowEventHandler<TDetail> = (detail: TDetail, event: CustomEvent<TDetail>) => void;

function emitWindowEvent<TDetail>(eventName: string, detail: TDetail): void {
  window.dispatchEvent(new CustomEvent<TDetail>(eventName, { detail }));
}

function subscribeWindowEvent<TDetail>(eventName: string, handler: WindowEventHandler<TDetail>): () => void {
  const listener: EventListener = (event) => {
    const customEvent = event as CustomEvent<TDetail>;
    handler(customEvent.detail, customEvent);
  };

  window.addEventListener(eventName, listener);
  return () => window.removeEventListener(eventName, listener);
}

export function emitTimelineAddEvent(detail: TimelineAddDetail): void {
  emitWindowEvent<TimelineAddDetail>(TIMELINE_ADD_EVENT, detail);
}

export function onTimelineAddEvent(handler: WindowEventHandler<TimelineAddDetail>): () => void {
  return subscribeWindowEvent(TIMELINE_ADD_EVENT, handler);
}

export function installSingletonTimelineAddEvent(
  key: string,
  handler: WindowEventHandler<TimelineAddDetail>,
): () => void {
  const listener: EventListener = (event) => {
    const customEvent = event as CustomEvent<TimelineAddDetail>;
    handler(customEvent.detail, customEvent);
  };

  const w = window as typeof window & {
    __oc_debug?: {
      __listeners?: Record<string, EventListener>;
    };
  };

  w.__oc_debug = w.__oc_debug || {};
  w.__oc_debug.__listeners = w.__oc_debug.__listeners || {};

  const previous = w.__oc_debug.__listeners[key];
  if (previous) window.removeEventListener(TIMELINE_ADD_EVENT, previous);

  w.__oc_debug.__listeners[key] = listener;
  window.addEventListener(TIMELINE_ADD_EVENT, listener);

  return () => {
    const current = w.__oc_debug?.__listeners?.[key];
    if (current === listener) {
      window.removeEventListener(TIMELINE_ADD_EVENT, listener);
      const listeners = w.__oc_debug?.__listeners;
      if (listeners) {
        delete listeners[key];
      }
    }
  };
}

export function emitMediaPanelSetMode(detail: MediaPanelSetModeDetail): void {
  emitWindowEvent<MediaPanelSetModeDetail>(OMNICINE_MEDIA_PANEL_SET_MODE_EVENT, detail);
}

export function onMediaPanelSetMode(handler: WindowEventHandler<MediaPanelSetModeDetail>): () => void {
  return subscribeWindowEvent(OMNICINE_MEDIA_PANEL_SET_MODE_EVENT, handler);
}
